import { Controller } from "@hotwired/stimulus"
import RequestHelper from "../helpers/request_helper"

function fetchJson(url, options) {
  return fetch(url, options).then((response) => response.json().then((data) => ({ ok: response.ok, data })));
}

// The team modal. Mounted at whatever ancestor contains BOTH its `.open-team-modal` triggers and
// its own `#team-modal`: `#teams-table-container` on the subdomain teams page, and `<body>` in the
// dashboard layout, whose header carries the trigger and whose layout renders the modal.
// `_modal.html.haml` carries no data-controller of its own, so it never gets a nested duplicate.
export default class extends Controller {
  static get targets() {
    return ["modal", "modalFooter", "searchBox", "searchLoading", "selectAllMembers", "searchResults"];
  }

  connect() {
    this.onOpenModal = this.onOpenModal.bind(this);
    this.onTabShown = this.onTabShown.bind(this);

    // .open-team-modal triggers live partly in this cluster's own markup and partly in the
    // dashboard header/members menu, rendered outside this cluster's views - delegation is kept
    // rather than a data-action, which could only reach the half of the triggers this lane owns.
    $(this.element).on("click.teamEditor", ".open-team-modal", this.onOpenModal);
    // shown.bs.tab is a Bootstrap-announced jQuery event, not a native DOM event - stays on
    // jQuery's side of the R8 boundary alongside the plugin that emits it.
    $(this.element).on("shown.bs.tab.teamEditor", ".nav-tabs li a", this.onTabShown);
  }

  disconnect() {
    $(this.element).off(".teamEditor");
    clearTimeout(this.searchTimer);
  }

  clearTabData() {
    if (this.hasSearchBoxTarget) { this.searchBoxTarget.value = ""; }
    if (this.hasModalTarget) {
      this.modalTarget.querySelectorAll(".tab-content .tab-pane .results").forEach((el) => { el.innerHTML = ""; });
    }
  }

  openModal() {
    this.clearTabData();
    if (this.hasSearchLoadingTarget) { this.searchLoadingTarget.style.display = ""; }
    $(this.modalTarget).modal("show");
  }

  // Public: also called from team_member_roles_controller.js's delete-collaborator handler
  // (crossing what used to be the $controller composition boundary) via
  // application.getControllerForElementAndIdentifier.
  loadTabData(url) {
    if (this.hasSearchLoadingTarget) { this.searchLoadingTarget.style.display = "none"; }
    this.clearTabData();

    fetchJson(url, RequestHelper.defaultRequestOptions()).then(({ ok, data }) => {
      if (this.hasSearchLoadingTarget) { this.searchLoadingTarget.style.display = "none"; }
      if (!ok) { return; }

      // data.header/data.footer/data.members/data.invitations are our own server's rendering of
      // the "team"/"modal_footer"/member-row partials (render_tab in the teams controller) - not
      // user input - so this is the accepted S1/S2/S4 case: server-rendered HTML from our own
      // origin. Kept on jQuery rather than outerHTML/insertAdjacentHTML deliberately (D4):
      // jQuery's html()/append() execute an embedded <script> the way ours never has, and
      // insertAdjacentHTML/outerHTML do not - switching is a behaviour change, not a pure retreat.
      // `.team-link` is queried fresh (not a Target) because replaceWith swaps the element itself;
      // a cached target reference would go stale the moment that happens.
      $(this.modalTarget).find(".team-link").replaceWith(data.header);
      if (this.hasModalFooterTarget) { $(this.modalFooterTarget).html(data.footer); }

      const activePane = this.modalTarget?.querySelector(".tab-content .tab-pane.active .results");
      if (activePane) {
        data.members.forEach((el) => $(activePane).append(el));
        data.invitations.forEach((el) => $(activePane).append(el));
      }
    });
  }

  // The page-global `[data-id=...]` lookup can mismatch tabMembersUrl/tabAddMembersUrl on the
  // dashboard layout's lone "add members" trigger, which has no sibling "members" trigger sharing
  // its data-id. Only reachable by then clicking the Members tab from that entry point.
  onOpenModal(event) {
    event.preventDefault();

    const anchor = event.currentTarget.href.split("#")[1];
    const membersTab = this.modalTarget.querySelector(".nav-tabs li:first-child a");
    const addMembersTab = this.modalTarget.querySelector(".nav-tabs li:last-child a");
    const teamId = event.currentTarget.dataset.id;

    const tabMembersUrl = document.querySelector(`[data-id="${teamId}"]`)?.getAttribute("href");
    const tabAddMembersUrl = document.querySelector(`[data-id="${teamId}"][data-select="add"]`)?.getAttribute("href");

    membersTab.setAttribute("data-url", tabMembersUrl);
    addMembersTab.setAttribute("data-url", tabAddMembersUrl);

    if (anchor === "members") {
      if (membersTab.parentElement.classList.contains("active")) { this.loadTabData(event.currentTarget.href); }
      membersTab.click();
    } else {
      if (addMembersTab.parentElement.classList.contains("active")) { this.loadTabData(event.currentTarget.href); }
      addMembersTab.click();
    }

    this.openModal();
  }

  onTabShown(event) {
    const url = event.currentTarget.getAttribute("data-url");
    if (url === null) { return; }
    this.loadTabData(url);
  }

  onSelectAll(event) {
    const checked = event.currentTarget.checked;
    if (this.hasSearchResultsTarget) {
      this.searchResultsTarget.querySelectorAll("input[type=checkbox]").forEach((el) => { el.checked = checked; });
    }
  }

  onSearchKeyup(event) {
    clearTimeout(this.searchTimer);
    this.searchTimer = setTimeout(() => {
      event.stopPropagation();
      const textBox = event.currentTarget;
      const text = textBox.value;
      const slug = this.modalTarget.querySelector(".team.with-slug")?.dataset.slug;
      const url = `${textBox.dataset.url}&q=${text}&slug=${slug}`;
      if (this.hasSearchLoadingTarget) { this.searchLoadingTarget.style.display = ""; }
      if (this.hasSelectAllMembersTarget) { this.selectAllMembersTarget.checked = false; }

      fetchJson(url, RequestHelper.defaultRequestOptions()).then(({ data }) => {
        if (this.hasSearchResultsTarget) {
          this.searchResultsTarget.querySelectorAll(".team-member, .no-results").forEach((el) => el.remove());
          // data.users is server-rendered from our own origin (D4/S4: kept on jQuery's .append,
          // not insertAdjacentHTML - see the comment in loadTabData for why).
          if (data.users.length > 0) {
            data.users.forEach((el) => $(this.searchResultsTarget).append(el));
          } else {
            $(this.searchResultsTarget).append("<div class='no-results'>No members found.</div>");
          }
        }
        if (this.hasSearchLoadingTarget) { this.searchLoadingTarget.style.display = "none"; }
      });
    }, 500);
  }

  onAddMembers(event) {
    event.preventDefault();

    const button = event.currentTarget;
    const checkboxes = this.hasSearchResultsTarget ? this.searchResultsTarget.querySelectorAll("input[type=checkbox]:checked") : [];
    const url = button.href;
    const htmlDatatype = button.dataset.datatype === "html";

    button.classList.add("disabled");

    const idsToAdd = Array.from(checkboxes).map((el) => ({
      email: el.dataset.email,
      role: el.closest(".team-member, tr")?.querySelector(".action select, .role select")?.value,
    }));

    if (idsToAdd.length === 0) {
      window.ltAlert({ message: "You must select at least one user to invite.", type: "toast-error", duration: 2500 });
      button.classList.remove("disabled");
      return;
    }

    const options = RequestHelper.defaultPostOptions();
    options.headers = Object.assign({}, options.headers, {
      "Content-Type": "application/json",
      "Accept": htmlDatatype ? "text/html" : "application/json",
    });
    options.body = JSON.stringify({ invitations: idsToAdd });

    fetchJson(url, options).then(({ ok, data }) => {
      if (!ok) {
        button.classList.remove("disabled");
        if (this.hasSearchLoadingTarget) { this.searchLoadingTarget.style.display = "none"; }
        window.ltAlert({ message: data.message, type: "toast-error", duration: 2500 });
        return;
      }

      if (htmlDatatype) {
        // Our own controller's JSON response, same origin as this page - asserted before
        // navigating per the S3 rule rather than trusting `data.redirect_url` outright.
        if (RequestHelper.isSameOriginUrl(data.redirect_url)) {
          window.location.href = data.redirect_url;
        } else {
          button.classList.remove("disabled");
          if (this.hasSearchLoadingTarget) { this.searchLoadingTarget.style.display = "none"; }
          window.ltAlert({ message: "An error occurred while completing this action.", type: "toast-error", duration: 2500 });
        }
      } else {
        button.classList.remove("disabled");
        if (this.hasSearchLoadingTarget) { this.searchLoadingTarget.style.display = "none"; }
        this.modalTarget.querySelector(".nav-tabs li:first-child a")?.click();
        window.ltAlert({ message: data.message, duration: 2500 });
      }
    });
  }
}
